import * as Yup from 'yup'

import type {
  DynamicFieldValues,
  EmrCodeDynamicField,
} from 'features/Procedure/state/procedure-api.type'

import {
  getMissingRequiredDynamicFields,
  getNonIntegerDynamicFields,
  getTooLongStringDynamicFields,
  STRING_FIELD_MAX_LENGTH,
} from './dynamic-fields.utils'

const formatDynamicFieldLabel = (label: string | undefined): string =>
  (label ?? '').replace(/:\s*$/, '').trim()

export const emrCodeSchema = Yup.object({
  active: Yup.number().required(),
  code: Yup.string().required(),
  code_text: Yup.string().nullable().optional(),
  code_text_short: Yup.string().nullable().optional(),
  code_type: Yup.string().required(),
  id: Yup.number().required(),
})

export const validationSchema = Yup.object({
  activeTab: Yup.string().oneOf(['cpt', 'icd']).required(),
  cpt4_code: Yup.mixed().when('activeTab', {
    is: 'icd',
    then: () => emrCodeSchema.nullable().optional(),
    otherwise: () => emrCodeSchema.required('Procedure is required').nullable(),
  }),
  icd10_codes: Yup.mixed().when('activeTab', {
    is: 'icd',
    then: () =>
      Yup.array().of(emrCodeSchema).min(1, 'At least one ICD code is required').required(),
    otherwise: () => Yup.array().of(emrCodeSchema),
  }),
  procedure_units: Yup.string().nullable().optional(),
  units: Yup.number().typeError('Units must be a number').positive().required('Units is required'),
  price: Yup.number()
    .typeError('Price must be a number')
    .positive()
    .min(0, 'Price must be greater than or equal to 0')
    .required('Price is required'),
  discount_type_id: Yup.string().nullable().optional(),
  dynamic_fields: Yup.mixed<EmrCodeDynamicField[] | undefined>(),
  dynamic_field_values: Yup.mixed<DynamicFieldValues | undefined>().test({
    name: 'required-dynamic-fields',
    test(value) {
      const parent = this.parent as {
        activeTab?: string
        dynamic_fields?: EmrCodeDynamicField[]
      }

      if (parent.activeTab === 'icd') {
        return true
      }

      const fieldValues = value as DynamicFieldValues | undefined

      const fieldErrors = [
        ...getMissingRequiredDynamicFields(parent.dynamic_fields, fieldValues).map((field) =>
          this.createError({
            path: `dynamic_field_values.${field.name}`,
            message: `${formatDynamicFieldLabel(field.label)} is required`,
          }),
        ),
        ...getNonIntegerDynamicFields(parent.dynamic_fields, fieldValues).map((field) =>
          this.createError({
            path: `dynamic_field_values.${field.name}`,
            message: `${formatDynamicFieldLabel(field.label)} must be a whole number`,
          }),
        ),
        ...getTooLongStringDynamicFields(parent.dynamic_fields, fieldValues).map((field) =>
          this.createError({
            path: `dynamic_field_values.${field.name}`,
            message: `${formatDynamicFieldLabel(
              field.label,
            )} must be at most ${STRING_FIELD_MAX_LENGTH} characters`,
          }),
        ),
      ]

      if (!fieldErrors.length) {
        return true
      }

      return new Yup.ValidationError(fieldErrors)
    },
  }),
})

export type FormValues = Yup.InferType<typeof validationSchema>
